import { SubmitHandler, useFieldArray } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Asterisk, Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { createWebhook, getWebhookById, getWebhookEvents, updateWebhook } from 'api/webhooks';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { queryClient } from 'queries/queryClient';
import { webhookQueryKeys } from 'queries/webhook-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface WebhookFormValues {
    name: string;
    url: string;
    enabled: boolean;
    events: { name: string; label: string; description: string; checked: boolean }[];
}

const WebhookDrawer = () => {
    const toast = useToast();
    const { webhookId } = useParams<{ webhookId: string }>();
    const isEditing = !!webhookId;
    const { accountId } = useCurrentAccount();
    const { goBack } = useDialogRoute();

    const { data: webhook } = useSuspenseQuery({
        queryKey: webhookQueryKeys.webhookById(accountId, webhookId),
        queryFn: () =>
            conditionallyFetchOrNull(() => getWebhookById(accountId, webhookId), isEditing),
    });

    const { data: eventOptions } = useSuspenseQuery({
        queryKey: webhookQueryKeys.webhookEvents(accountId),
        queryFn: () => getWebhookEvents(),
        staleTime: Infinity,
        select: data =>
            data.map(item => ({
                name: item.name,
                label: item.label,
                description: item.description,
                checked: webhook?.events.includes(item.name),
            })),
    });

    const methods = Form.useGivebutterForm<WebhookFormValues>({
        defaultValues: {
            name: webhook?.name ?? '',
            url: webhook?.url ?? '',
            enabled: webhook?.enabled ?? true,
            events: eventOptions,
        },
    });
    const {
        formState: { isDirty, isValid },
        handleSubmit,
        control,
        path,
        resetFormButton,
        setError,
    } = methods;
    const eventsFieldArray = useFieldArray({
        name: 'events',
        control,
    });
    const { fields: eventFields } = eventsFieldArray;

    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey: webhookQueryKeys.webhooks(accountId) });
        goBack();
        toast.success(`${isEditing ? 'Updated' : 'Created'} webhook`);
    };

    const onError = (e: GivebutterAxiosError) => {
        const data = e.response?.data;
        const errors = data?.errors;

        toast.error(data?.message ?? `Failed to ${isEditing ? 'update' : 'create'} webhook`);

        if (typeof errors === 'object') {
            for (let field in errors) {
                if (field === 'events') {
                    errors[field].map((err: string, index: number) =>
                        setError(`events.${index}.checked`, {
                            type: 'server',
                            message: err,
                        }),
                    );
                    continue;
                }

                setError(field as keyof WebhookFormValues, {
                    message: errors[field][0],
                });
            }
        }

        resetFormButton();
    };

    const { mutate: create, isPending: isCreating } = useMutation({
        mutationFn: (values: WebhookFormValues) =>
            createWebhook(accountId, {
                ...values,
                events: values.events.filter(item => item.checked).map(item => item.name),
            }),
        onSuccess,
        onError,
    });

    const { mutate: update, isPending: isUpdating } = useMutation({
        mutationFn: (values: WebhookFormValues) =>
            updateWebhook(accountId, webhookId, {
                ...values,
                events: values.events.filter(item => item.checked).map(item => item.name),
            }),
        onSuccess,
        onError,
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(
                    (isEditing ? update : create) as SubmitHandler<WebhookFormValues>,
                )}
            >
                <Drawer.Header>
                    <Drawer.Title>{isEditing ? 'Edit' : 'New'} webhook</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-4">
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="name">
                                <Form.TextInputForm placeholder="My webhook" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('url')}>
                                Webhook URL
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={path('url')}
                                rules={{ required: 'Url is required' }}
                            >
                                <Form.TextInputForm placeholder="https://example.org/my-webhook-handler" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('url')} />
                        </Form.Container>

                        <Form.Container>
                            <Label>
                                Events
                                <Asterisk />
                            </Label>
                            <div className="flex flex-col gap-2">
                                {eventFields.map((field, index) => (
                                    <div key={field.name} className="flex gap-2">
                                        <Form.UseFormCheckboxInput
                                            id={field.name}
                                            value={field.name}
                                            control={control}
                                            name={`events.${index}.checked`}
                                            checked={field.checked}
                                            className="mt-1"
                                        />

                                        <div>
                                            <Form.Label name={`events.${index}.checked`}>
                                                {field.label}
                                            </Form.Label>
                                            <p className={'text-sm text-gray-600'}>
                                                {field.description}
                                            </p>
                                            <Form.UseFormError
                                                name={path(`events.${index}.checked`)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Form.Container>
                        <div className={'flex justify-between gap-y-1'}>
                            <div className={'flex flex-col gap-y-1'}>
                                <Form.Label name={path('enabled')}>Enabled</Form.Label>
                                <p className={'text-sm text-gray-600'}>
                                    When disabled, this webhook will not be triggered.
                                </p>
                            </div>
                            <Form.Switch name={path('enabled')} control={control} />
                        </div>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton
                            isSubmitting={isCreating || isUpdating}
                            disabled={!isDirty || !isValid}
                        >
                            {isEditing ? 'Save' : 'Create'}
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { WebhookDrawer };
